import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Plus, Trash2, Users } from 'lucide-react';
import { type FormEvent, useMemo, useState, useId } from 'react';
import { useMe } from '~/api/auth';
import { formError, inlineError } from '~/api/client';
import {
  type CreatePersonBody,
  type Department,
  departmentsQueryOptions,
  peopleQueryOptions,
  useCreateDepartment,
  useCreatePerson,
  useDeleteDepartment,
} from '~/api/people';
import { ROLE_LABEL, STATUS_LABEL } from '~/lib/format';
import { notify } from '~/lib/notify';
import { Avatar } from '~/ui/Avatar';
import { Button } from '~/ui/Button';
import { useConfirm } from '~/ui/Confirm';
import { Dialog } from '~/ui/Dialog';
import { EmptyState } from '~/ui/EmptyState';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { Input } from '~/ui/Input';
import { InviteLinkOnce } from '~/ui/InviteLinkOnce';
import { Notice } from '~/ui/Notice';
import { PageHeader } from '~/ui/PageHeader';
import { Panel } from '~/ui/Panel';
import { Pill } from '~/ui/Pill';
import { STATUS_TONE } from '~/ui/tones';
import { Select } from '~/ui/Select';
import { Skeleton } from '~/ui/Skeleton';
import { WorkModeSelect } from '~/features/people/WorkModeSelect';
import { Switch } from '~/ui/Switch';

export const Route = createFileRoute('/_app/people/')({
  component: PeoplePage,
});

function PeoplePage() {
  const me = useMe();
  const isOwner = me.person.role === 'owner';
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'' | 'invited' | 'active' | 'disabled'>('');
  const [adding, setAdding] = useState(false);

  const people = useQuery(peopleQueryOptions(status ? { status } : {}));
  const departments = useQuery(departmentsQueryOptions);
  const deptName = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of departments.data ?? []) map.set(d.id, d.name);
    return (id: string | null) => (id ? (map.get(id) ?? '—') : '—');
  }, [departments.data]);

  const filtered = useMemo(() => {
    const list = people.data ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((p) =>
      [p.name, p.email, p.title ?? '', p.empCode ?? ''].some((s) =>
        s.toLowerCase().includes(needle),
      ),
    );
  }, [people.data, q]);

  return (
    <div className="mx-auto max-w-[1100px]">
      <PageHeader
        eyebrow="Team"
        title="People"
        subtitle={people.data ? `${people.data.length} in view` : undefined}
        actions={
          isOwner && (
            <Button icon={<Plus size={16} aria-hidden />} onClick={() => setAdding(true)}>
              Add person
            </Button>
          )
        }
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_180px]">
            <Input
              label="Search"
              placeholder="Name, email, title or employee code"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
            >
              <option value="">Everyone</option>
              <option value="active">Active</option>
              <option value="invited">Invited</option>
              <option value="disabled">Disabled</option>
            </Select>
          </div>

          {people.isPending ? (
            <Skeleton lines={6} />
          ) : people.isError ? (
            <ErrorNotice error={people.error} onRetry={() => people.refetch()} />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<Users aria-hidden />}
              title={
                q || status ? 'Nobody matches those filters.' : 'Nobody is here yet apart from you.'
              }
              action={
                q || status ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setQ('');
                      setStatus('');
                    }}
                  >
                    Clear filters
                  </Button>
                ) : isOwner ? (
                  <Button size="sm" onClick={() => setAdding(true)}>
                    Add the first person
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <ul className="divide-y divide-line rounded-panel border border-line">
              {filtered.map((p) => (
                <li key={p.id}>
                  <Link
                    to="/people/$id"
                    params={{ id: p.id }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-paper-2"
                  >
                    <Avatar name={p.name} personId={p.id} photoId={p.avatarFileId} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{p.name}</span>
                        {p.id === me.person.id && <Pill tone="accent">You</Pill>}
                      </div>
                      <div className="truncate text-sm text-ink-3">
                        {p.email}
                        {p.title ? ` · ${p.title}` : ''}
                      </div>
                    </div>
                    <div className="hidden text-sm text-ink-2 sm:block">
                      {deptName(p.departmentId)}
                    </div>
                    <Pill>{ROLE_LABEL[p.role]}</Pill>
                    <Pill tone={STATUS_TONE[p.status]}>{STATUS_LABEL[p.status]}</Pill>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DepartmentsPanel
          departments={departments.data ?? []}
          loading={departments.isPending}
          canEdit={isOwner}
        />
      </div>

      {isOwner && (
        <AddPersonDialog
          open={adding}
          onClose={() => setAdding(false)}
          departments={departments.data ?? []}
        />
      )}
    </div>
  );
}

function DepartmentsPanel({
  departments,
  loading,
  canEdit,
}: {
  departments: Department[];
  loading: boolean;
  canEdit: boolean;
}) {
  const [name, setName] = useState('');
  const create = useCreateDepartment();
  const remove = useDeleteDepartment();
  const confirm = useConfirm();
  const createMessage = inlineError(create.error);

  const onAdd = async (e: FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 2) return;
    try {
      await create.mutateAsync({ name: name.trim() });
      setName('');
    } catch {
      // shown inline
    }
  };

  const onRemove = async (d: Department) => {
    if (
      !(await confirm({
        title: `Remove ${d.name}?`,
        body: `Its ${d.personCount} people will have no department.`,
        confirmLabel: 'Remove',
        tone: 'danger',
      }))
    )
      return;
    try {
      const res = await remove.mutateAsync(d.id);
      notify.success(
        `${d.name} removed.`,
        res.movedPersonCount ? `${res.movedPersonCount} people now have no department.` : undefined,
      );
    } catch (err) {
      notify.error('Could not remove the department.', err);
    }
  };

  return (
    <Panel title="Departments" eyebrow="Structure">
      {loading ? (
        <Skeleton lines={4} />
      ) : departments.length === 0 ? (
        <p className="text-sm text-ink-3">No departments yet.</p>
      ) : (
        <ul className="divide-y divide-line">
          {departments.map((d) => (
            <li key={d.id} className="flex items-center gap-2 py-2">
              <span className="flex-1 text-sm">{d.name}</span>
              <span className="font-mono text-xs text-ink-3 tabular">{d.personCount}</span>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => onRemove(d)}
                  aria-label={`Remove ${d.name}`}
                  className="grid size-7 place-items-center rounded-control text-ink-3 hover:bg-late-soft hover:text-late"
                >
                  <Trash2 size={14} aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <form onSubmit={onAdd} className="mt-3 flex items-end gap-2">
          <div className="flex-1">
            <Input
              label="New department"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={createMessage ?? undefined}
            />
          </div>
          <Button
            type="submit"
            variant="secondary"
            loading={create.isPending}
            disabled={name.trim().length < 2}
          >
            Add
          </Button>
        </form>
      )}
    </Panel>
  );
}

const ROLE_HELP: Record<CreatePersonBody['role'] & string, string> = {
  member: 'Sees and runs their own duties.',
  sub_manager: 'Also makes teams in their department, hands out and reviews their work.',
  manager: 'Also sees their department’s people and work.',
  owner: 'Sees and changes everything in the company.',
};

function AddPersonDialog({
  open,
  onClose,
  departments,
}: {
  open: boolean;
  onClose: () => void;
  departments: Department[];
}) {
  const create = useCreatePerson();
  // Unique per dialog, so its footer button submits this form and no other.
  const addPersonId = useId();
  const [form, setForm] = useState<CreatePersonBody>({
    name: '',
    email: '',
    role: 'member',
    clockRequired: true,
  });
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const { fields: fieldErrors, message: formMessage } = formError(create.error);

  const reset = () => {
    setForm({ name: '', email: '', role: 'member', clockRequired: true });
    setInviteUrl(null);
    create.reset();
  };

  const close = () => {
    reset();
    onClose();
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const res = await create.mutateAsync({
        ...form,
        departmentId: form.departmentId || null,
        title: form.title?.trim() || null,
        empCode: form.empCode?.trim() || null,
      });
      setInviteUrl(res.inviteUrl);
    } catch {
      // shown inline
    }
  };

  if (inviteUrl) {
    return (
      <Dialog
        open={open}
        onClose={close}
        title="Send them this link."
        footer={<Button onClick={close}>Done</Button>}
        dismissible={false}
      >
        <InviteLinkOnce url={inviteUrl} />
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Who is joining?"
      footer={
        <>
          <Button variant="quiet" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" form={addPersonId} loading={create.isPending}>
            Add person
          </Button>
        </>
      }
    >
      <form id={addPersonId} onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          label="Name"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          error={fieldErrors['name']}
        />
        <Input
          label="Email"
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          error={fieldErrors['email']}
          hint="They set their own password from the invite link."
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Role"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as CreatePersonBody['role'] })}
            hint={ROLE_HELP[form.role ?? 'member']}
          >
            <option value="member">Member</option>
            <option value="sub_manager">Sub manager</option>
            <option value="manager">Manager</option>
            <option value="owner">Owner</option>
          </Select>
          <Select
            label="Department"
            value={form.departmentId ?? ''}
            onChange={(e) => setForm({ ...form, departmentId: e.target.value || null })}
            optional
          >
            <option value="">None</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Title"
            optional
            value={form.title ?? ''}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            error={fieldErrors['title']}
          />
          <Input
            label="Employee code"
            optional
            mono
            value={form.empCode ?? ''}
            onChange={(e) => setForm({ ...form, empCode: e.target.value })}
            error={fieldErrors['empCode']}
          />
        </div>
        <Switch
          label="Clock required"
          hint="Off for people measured by output who never clock in."
          checked={form.clockRequired ?? true}
          onChange={(v) => setForm({ ...form, clockRequired: v })}
        />
        <WorkModeSelect
          value={form.workMode ?? 'office'}
          onChange={(v) => setForm({ ...form, workMode: v })}
        />
        {formMessage && <Notice tone="late">{formMessage}</Notice>}
      </form>
    </Dialog>
  );
}
